import React, { useEffect, useState, useContext } from "react"
import { HiOutlineRefresh, HiOutlineClock, HiOutlineSearch, HiArrowRight, HiOutlineClipboardCopy, HiOutlineEye, HiOutlineShieldExclamation, HiOutlineServer } from "react-icons/hi"
import { Modal } from "components/Core"
import JsonViewer from "components/Core/JsonViewer"
import { Link } from "react-router-dom"
import { post_middleware_osrm_search } from "services/middleware_services"
import { format_datetime_by_local_client } from "helpers/datetime_helper"
import AuthContext from "store/context/PublicContext"

interface SourcesDashboardWidgetProps {
  title?: string
  actions?: string[]
  sortField?: string
  heightClass?: string
}

const SourcesDashboardWidget: React.FC<SourcesDashboardWidgetProps> = ({
  title = "Últimos movements de sources",
  actions = [],
  sortField = "_omnixMetadata.updatedAt",
  heightClass = "h-full"
}) => {
  const [records, setRecords] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedItem, setSelectedItem] = useState<any>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [authError, setAuthError] = useState(false)
  const [serverError, setServerError] = useState(false)



  const { user: { token } } = useContext(AuthContext)

  const fetchLatest = React.useCallback(async () => {
    let filters: Record<string, any> = {}

    setIsLoading(true)
    try {
      const res = await post_middleware_osrm_search(
        "sources",
        {
          from: 0,
          size: 10,
          filters
        },
        token
      )
      setRecords(res?.data?.message?.records || [])
      setAuthError(false)
      setServerError(false)
    } catch (err: any) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setAuthError(true)
      } else if (err?.response?.status === 404) {
        setRecords([])
      } else {
        console.error("Error fetching latest sources", err)
        setServerError(true)
      }
    } finally {
      setIsLoading(false)
    }
  }, [token])

  const actionsString = React.useMemo(() => JSON.stringify(actions), [actions])

  useEffect(() => {
    fetchLatest()
    const interval = setInterval(fetchLatest, 15000)
    return () => clearInterval(interval)
  }, [fetchLatest, actionsString])

  return (
    <div className={`w-full flex justify-center items-start pt-0 flex-1 min-h-0 ${heightClass}`}>
      <div className="w-full bg-white rounded-none xl:border-t-0 border-gray-100 flex flex-col h-full" style={{ maxHeight: "100%" }}>
        
        {/* Header */}
        <div className="px-5 py-4 border-b border-gray-100 flex flex-row items-center justify-between bg-white relative z-10 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></div>
            <h3 className="text-[13px] font-black text-gray-800 uppercase tracking-widest">
              {title}
            </h3>
          </div>
          <button 
            onClick={fetchLatest}
            disabled={isLoading}
            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors"
          >
            <HiOutlineRefresh className={`text-[15px] ${isLoading ? "animate-spin text-indigo-400" : ""}`} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 hide-scrollbar relative">
          {authError ? (
            <div className="p-8 flex flex-col items-center justify-center gap-2 h-full text-center">
              <div className="w-12 h-12 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mb-1">
                <HiOutlineShieldExclamation className="text-2xl" />
              </div>
              <p className="text-[13px] font-bold text-gray-800">Sesión Expirada</p>
              <p className="text-[11px] font-medium text-gray-500 max-w-[220px] mb-1">
                Tus credenciales han caducado o careces de los privilegios necesarios.
              </p>
              <button 
                onClick={() => { localStorage.clear(); window.location.href = "/" }}
                className="px-4 py-2 mt-1 bg-red-50 text-red-600 hover:bg-red-100 rounded-md text-[11px] font-bold transition-colors"
              >
                Iniciar sesión nuevamente
              </button>
            </div>
          ) : serverError ? (
            <div className="p-8 flex flex-col items-center justify-center gap-2 h-full text-center">
              <HiOutlineServer className="text-3xl text-red-300" />
              <p className="text-[13px] font-bold text-gray-500">Fallo de conectividad</p>
              <p className="text-[11px] font-medium text-gray-400 max-w-[200px]">
                No se pudo conectar con OSRM. Verifica tu red.
              </p>
              <button 
                onClick={fetchLatest}
                className="px-4 py-2 mt-2 bg-gray-50 text-indigo-600 hover:bg-gray-100 rounded-md text-[11px] font-bold transition-colors"
              >
                Reintentar
              </button>
            </div>
          ) : isLoading && records.length === 0 ? (
            <div className="p-8 flex flex-col items-center justify-center gap-3 h-full">
              <span className="h-5 w-5 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin"></span>
              <p className="text-[12px] font-medium text-gray-400">Cargando datos...</p>
            </div>
          ) : records.length > 0 ? (
            <div className="flex flex-col divide-y divide-gray-50">
              {records.map((item: any, idx: number) => {
                const dateStr = item?._omnixMetadata?.updatedAt || item?._omnixMetadata?.createdAt || item?.custom?.lastUpdateDate || item?.lastUpdateDate
                let fechaFormat = "--"
                if (dateStr && !isNaN(new Date(dateStr).getTime())) {
                  fechaFormat = format_datetime_by_local_client(new Date(dateStr), "dd MMM yyyy, HH:mm")
                } else if (dateStr && !isNaN(parseInt(dateStr))) {
                  fechaFormat = format_datetime_by_local_client(new Date(parseInt(dateStr)), "dd MMM yyyy, HH:mm")
                }

                const estado = item?.enabled ? "Activo" : "Inactivo"
                const locationStr = item?.location?.city || item?.location?.commune || item?.location?.address || item?.location?.country || "Sin Ubicación"

                return (
                  <div key={idx} className="p-4 hover:bg-gray-50/80 transition-colors group">
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[15px] font-black text-gray-900 truncate tracking-tight">
                            {item.name || `Source #${item.id?.slice(-8)}`}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-2 mt-0.5">
                          <div className="flex items-center gap-1">
                            <span className="text-[11px] font-semibold text-indigo-500 truncate text-opacity-80">
                              ID: {item.id}
                            </span>
                            <button 
                              onClick={() => {
                                navigator.clipboard.writeText(item.id)
                                setCopiedId(`id-${item.id}`)
                                setTimeout(() => setCopiedId(null), 1500)
                              }}
                              className="text-gray-400 hover:text-indigo-600 transition-colors ml-1 p-0.5 rounded focus:ring-1 focus:ring-indigo-200"
                              title="Copiar ID"
                            >
                              <HiOutlineClipboardCopy className={`text-[13px] ${copiedId === `id-${item.id}` ? "text-emerald-500" : ""}`} />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0 flex flex-col items-end gap-1">
                        <div className="shrink-0 flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide">
                          {locationStr}
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white border border-gray-100 shadow-sm rounded p-2.5 mt-2 flex flex-col gap-1.5 hover:border-indigo-100 transition-colors group/card">
                       <div className="flex items-center justify-between pb-1.5 border-b border-gray-50">
                        <span className={`text-[11.5px] font-semibold truncate mr-2 ${item?.enabled ? 'text-emerald-600' : 'text-gray-500'}`}>
                          {estado}
                        </span>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="flex items-center gap-1 text-[10px] font-medium text-gray-400 whitespace-nowrap">
                            <HiOutlineClock className="text-[12px] opacity-70" />
                            {fechaFormat}
                          </div>
                          <button
                            onClick={() => setSelectedItem(item)}
                            className="opacity-0 group-hover/card:opacity-100 px-2 py-0.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 text-[9px] font-bold uppercase rounded transition-all"
                          >
                            <HiOutlineEye className="inline mr-1 text-[11px]" />
                            Detalles
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="p-8 flex flex-col items-center justify-center gap-2 h-full text-center">
              <HiOutlineSearch className="text-3xl text-gray-300" />
              <p className="text-[13px] font-bold text-gray-500">Sin movimientos</p>
              <p className="text-[11px] font-medium text-gray-400 max-w-[200px]">
                No hay actividad reciente.
              </p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="p-3 border-t border-gray-100 bg-gray-50/50 flex justify-center shrink-0">
          <Link 
            to="/sources" 
            className="w-full py-2 flex items-center justify-center gap-2 text-[12px] font-bold text-indigo-600 bg-indigo-50/50 hover:bg-indigo-100/50 hover:text-indigo-700 rounded-lg transition-colors"
          >
            Ver listado completo
            <HiArrowRight className="text-sm" />
          </Link>
        </div>

      </div>

      <Modal mounted={!!selectedItem} localIconClose={false} className="w-[600px] max-w-[92vw] !p-0 !rounded-[24px] shadow-2xl bg-[#F2F2F7]/95 backdrop-blur-2xl border border-white/40 overflow-hidden tracking-tight">
        {selectedItem && (
          <div className="w-full flex flex-col h-full max-h-[85vh] relative">
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-9 h-1 bg-gray-300/80 rounded-full z-20"></div>
            <div className="px-4 pt-5 pb-3.5 border-b border-gray-200/50 flex items-center justify-center bg-white/70 relative shrink-0">
              <h3 className="text-[16px] font-bold text-gray-900 mt-1">
                Detalles RAW
              </h3>
              <button 
                type="button"
                onClick={(e) => { e.stopPropagation(); setSelectedItem(null); }} 
                className="absolute right-4 bottom-3.5 text-[16px] font-semibold text-indigo-600 active:text-indigo-800 transition-colors"
                style={{ WebkitTapHighlightColor: "transparent" }}
              >
                Listo
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 hide-scrollbar">
              <div className="mb-2 px-1">
                <span className="text-[12px] font-bold text-gray-500 uppercase tracking-wider ml-1">
                  Source <span className="font-medium opacity-80 normal-case ml-1">#{selectedItem.id}</span>
                </span>
              </div>
              <div className="bg-white rounded-[16px] overflow-hidden shadow-sm border border-gray-100">
                <div className="w-full p-4 overflow-x-auto text-[13px]">
                  <JsonViewer data={selectedItem} />
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>



    </div>
  )
}

export default SourcesDashboardWidget
